import { Button, Form, Image, Input, message, Modal, Upload } from 'antd';
import { concat, filter, findIndex, includes, map } from 'lodash';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import {
  ALLOWED_IMAGE_TYPES,
  DATETIMEWITHDIVIDE,
  ELEMENT_IDS,
  FILE_SIZE_20MB,
  MAX_FILES_COUNT,
  REQUEST_FEATURE_UPGRADE_KEYS,
} from '../../common/constants';
import {
  addWatermark,
  compressImage,
  dataURLtoFile,
  fileUpload,
  formValidatorRules,
  getBase64,
} from '../../common/utils';
import UpgradeModal from '../../components/UpgradeModal';
import { GET_URLS } from '../approver/graphql/Queries';
import { GET_URL_BY_KEY } from '../profile/graphql/Queries';

const { required } = formValidatorRules;

const RegisterCommentModal = (props) => {
  const {
    showModal,
    setShowModal,
    data,
    fieldData,
    setCommentData,
    commentData,
    setAdditionalComments,
    additionalComments,
    setIsAdditionalPoint,
    isAdditionalPoint = false,
    editingCommentIndex = null,
    setEditingCommentIndex,
    fieldType = 'detail',
    rowIndex = null,
  } = props;

  const registerLogData = data;
  const { getTenantUser } = useContext(AppContext);
  const tenantUser = getTenantUser();

  const [form] = Form.useForm();
  const { TextArea } = Input;
  const [previewImage, setPreviewImage] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [showImageSizeError, setShowImageSizeError] = useState(false);
  let files;

  const handleCancel = () => {
    setShowModal(false);
    if (isAdditionalPoint) {
      setIsAdditionalPoint(false);
      if (setEditingCommentIndex) {
        setEditingCommentIndex(null);
      }
    }
  };

  const [getImagesSignedUrls, { data: images }] = useLazyQueryWithCallbacks(GET_URL_BY_KEY, {
    fetchPolicy: 'network-only',
  });

  const updateState = (urls = []) => {
    const comment = form.getFieldValue('comment');
    if (isAdditionalPoint) {
      const newComment = {
        remark: comment,
        assets: imageList ? concat(imageList, urls) : urls,
      };

      setAdditionalComments((prevComments) => {
        const updatedComments = [...(prevComments || [])];
        if (editingCommentIndex !== null) {
          // Update existing comment
          updatedComments[editingCommentIndex] = newComment;
        } else {
          // Add new comment
          updatedComments.push(newComment);
        }
        return updatedComments;
      });

      if (setEditingCommentIndex) {
        setEditingCommentIndex(null);
      }
    } else {
      const commentObj = {
        fieldId: fieldData?.id,
        fieldType,
        rowIndex,
        remark: comment,
        images: imageList ? concat(imageList, urls) : urls,
        audios: [],
      };

      setCommentData((oldData) => {
        const newComments = [...(oldData?.comments || [])];
        const index = findIndex(
          newComments,
          (c) =>
            c?.fieldId === fieldData?.id &&
            c?.fieldType === fieldType &&
            c?.rowIndex === rowIndex,
        );
        if (index === -1) {
          newComments.push(commentObj);
        } else {
          newComments.splice(index, 1, commentObj);
        }
        return {
          ...oldData,
          comments: newComments,
        };
      });
    }
    setDisabled(false);
    setShowModal(false);
  };

  const [fetchSignedUrl] = useLazyQueryWithCallbacks(GET_URLS, {
    fetchPolicy: 'network-only',
    onCompleted: async (response) => {
      const filteredFileList = filter(fileList, 'name');
      const promises = map(
        response?.getSignedPutUrls?.signedUrls,
        async (imageUrl, index) => {
          const callBack = async (image) => {
            const watermarkedImageUrl = await addWatermark(image);
            const file = dataURLtoFile(
              watermarkedImageUrl,
              filteredFileList?.[index]?.originFileObj?.name,
            );
            await fileUpload(imageUrl, file).catch(() => {
              return message.error('Image upload failed!');
            });
          };
          await compressImage({
            file: filteredFileList?.[index]?.originFileObj,
            fileType: filteredFileList?.[index]?.type?.split('/')?.[1],
            callBack,
          });
        },
      );
      await Promise.all(promises);

      // Determine asset type based on context
      let assetType = 'GENERAL_REMARK';
      if (isAdditionalPoint) {
        assetType = 'ADDITIONAL_REMARK';
      } else if (fieldType === 'detail') {
        assetType = 'DETAILS_REMARK';
      } else if (fieldType === 'formField') {
        assetType = 'FIELD_REMARK';
      }

      const assets = map(response?.getSignedPutUrls?.keys, (key, index) => ({
        type: assetType,
        key,
        assetType: 'IMAGE',
        size: fileList?.[index]?.size,
        isImageCaptured: false,
        capturedAt: moment(),
      }));
      updateState(assets);
    },
  });

  const handleOnChange = (info) => {
    const newFileList = filter(info?.fileList, (file) => {
      setShowImageSizeError(false);
      if (file?.size > FILE_SIZE_20MB) {
        setShowImageSizeError(true);
        return false;
      }
      if (!file?.url && !includes(ALLOWED_IMAGE_TYPES, file?.type)) {
        message.destroy();
        message.error('File type should be PNG, JPG, JPEG');
        return false;
      }
      return file;
    });
    setFileList(newFileList);
  };

  const handleUploadImage = async () => {
    setDisabled(true);
    const fileSend = (anotherFileList) => {
      return map(anotherFileList, (file) => ({
        fileName: `register-approver/${file?.name}`,
        contentType: file?.type,
      }));
    };
    const filteredFileList = filter(fileList, 'name');
    if (filteredFileList?.length) {
      try {
        fetchSignedUrl({
          variables: {
            data: fileSend(filteredFileList),
          },
        });
      } catch (error) {
        setDisabled(false);
      }
    } else {
      updateState();
    }
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      const preview = await getBase64(file?.originFileObj);
      files = file.url || preview;
      setPreviewImage((oldData) => [...oldData, files]);
    }
  };

  const defaultList = (imageArr) => {
    return map(imageArr, (image, index) => ({
      uid: index,
      url: image,
    }));
  };

  useEffect(() => {
    if (images?.getImagesSignedUrls?.length > 0) {
      setFileList(defaultList(images?.getImagesSignedUrls));
    }
  }, [images]);

  useEffect(() => {
    if (!form.getFieldValue('comment')) {
      if (isAdditionalPoint) {
        // Load existing comment if editing
        const existingComment =
          editingCommentIndex !== null
            ? additionalComments?.[editingCommentIndex]
            : null;

        if (existingComment) {
          const commentImage = map(existingComment?.assets, 'key');
          if (commentImage?.length > 0) {
            getImagesSignedUrls({
              variables: {
                data: { keys: commentImage },
              },
            });
          }
          setFileList(defaultList(commentImage));
          setImageList(existingComment?.assets);
          form.setFieldsValue({
            comment: existingComment?.remark,
          });
        }
      } else {
        const existingComment = commentData?.comments?.find(
          (c) =>
            c?.fieldId === fieldData?.id &&
            c?.fieldType === fieldType &&
            c?.rowIndex === rowIndex,
        );
        if (existingComment) {
          const commentImages = map(existingComment?.images, `key`);
          if (commentImages?.length > 0) {
            getImagesSignedUrls({
              variables: {
                data: { keys: commentImages },
              },
            });
          }
          setFileList(defaultList(commentImages));
          setImageList(existingComment?.images);
          form.setFieldsValue({
            comment: existingComment?.remark,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentData, fieldData, additionalComments, editingCommentIndex]);

  const onFinish = async () => {
    await handleUploadImage();
  };

  const handleRemove = (info) => {
    const imageKey = info?.url?.split('/')?.[3]?.split('?')?.[0];
    if (isAdditionalPoint) {
      setImageList((prev) => filter(prev, (e) => e?.key !== imageKey));
    } else {
      setImageList((prev) => filter(prev, (e) => e?.key !== imageKey));
    }
  };

  return (
    <Modal
      maskClosable={false}
      centered
      open={showModal}
      onCancel={handleCancel}
      closable={false}
      footer={null}
    >
      {
        //! giving inline style as need to generate image from this div
      }
      <div style={{ opacity: 0, height: 0 }}>
        <div
          id={ELEMENT_IDS?.TIME_STAMP}
          style={{
            background: '#000',
            color: '#3366ff',
            width: 'max-content',
          }}
        >
          {`${moment()
            ?.tz(data?.register?.project?.timeZone)
            ?.format(DATETIMEWITHDIVIDE)} - ${
            registerLogData?.project?.timeZone
          }`}
        </div>
      </div>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="comment"
          label="Comment"
          rules={[
            required,
            {
              max: 1000,
              message: 'Comment cannot be more than 1000 characters',
            },
            { whitespace: true },
          ]}
        >
          <TextArea />
        </Form.Item>
        <div className="mb-10 text-secondary">Images</div>
        <Upload
          maxCount={tenantUser?.tenant?.registerAssetLimit || MAX_FILES_COUNT}
          accept=".png,.jpeg,.jpg"
          multiple
          listType="picture-card"
          fileList={fileList}
          onChange={(info) => {
            handleOnChange(info);
          }}
          beforeUpload={(_, ...rest) => {
            if (
              [...rest?.[0], ...fileList]?.length >
              tenantUser?.tenant?.registerAssetLimit
            ) {
              message.destroy();
              message.error(
                'Your current plan has restriction and requires upgrade.',
              );
            }
            return false;
          }}
          onPreview={(e) => {
            handlePreview(e);
          }}
          onRemove={(e) => handleRemove(e)}
          showUploadList={{ showPreviewIcon: false }}
        >
          <div
            className="height-percent-100 width-percent-100 d-flex align-center justify-center pointer"
            onClick={(e) => {
              if (
                fileList?.length ===
                (tenantUser?.tenant?.registerAssetLimit || MAX_FILES_COUNT)
              ) {
                e.stopPropagation();
                message.destroy();
                message.error(
                  'Your current plan has restriction and requires upgrade.',
                );
              }
            }}
          >
            <Button type="primary" shape="round">
              Add
            </Button>
          </div>
        </Upload>
        <div>
          {React.Children.map(previewImage, (photo) => (
            <Image src={photo} />
          ))}
        </div>
        {showImageSizeError && (
          <div className="mt-10 mb-10 text-danger">
            * Maximum file size is 20MB
          </div>
        )}
        <div className="form-buttons mt-8">
          <Button
            shape="round"
            className="cancel-button"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            shape="round"
            className="save-button"
            htmlType="submit"
            disabled={disabled}
            type="primary"
          >
            {disabled ? 'Uploading...' : 'Save'}
          </Button>
        </div>
      </Form>
      {showUpgradeModal && (
        <UpgradeModal
          showModal={showUpgradeModal}
          setShowModal={setShowUpgradeModal}
          projectId={registerLogData?.register?.project?.id}
          featureKey={REQUEST_FEATURE_UPGRADE_KEYS.INSPECTION_ASSET_LIMIT}
          isAlreadyRequested={
            tenantUser?.tenant?.featureApprovalRequestConfig
              ?.inspectionAssetLimitRequestSent
          }
        />
      )}
    </Modal>
  );
};

export default RegisterCommentModal;
